import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { orderRegistrationService } from '../../../../services/orderRegistrationService';
import { CreateOrderWithLinesRequest, CreateOrderLineRequest, Warehouse } from '../../../../types/orderRegistration';
import { ItemSearchSelect, SearchableItem } from '../../../common/ItemSearchSelect';

interface CreateOrderFormProps {
  onSuccess: (orderNo: number) => void;
}

interface OrderLineFormData extends CreateOrderLineRequest {
  id: string;
}

export const CreateOrderForm: React.FC<CreateOrderFormProps> = ({ onSuccess }) => {
  // Form state
  const [orderNoERP, setOrderNoERP] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [customerNo, setCustomerNo] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [delCustomerName, setDelCustomerName] = useState('');
  const [delAddress1, setDelAddress1] = useState('');
  const [delCity, setDelCity] = useState('');
  const [delZipCode, setDelZipCode] = useState('');
  const [lines, setLines] = useState<OrderLineFormData[]>([]);

  // Data state
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      // Load warehouses and generate OrderNoERP in parallel (items are searched on-demand)
      const [warehousesData, nextOrderNo] = await Promise.all([
        orderRegistrationService.getWarehouses(),
        orderRegistrationService.getNextOrderNo()
      ]);

      setWarehouses(warehousesData.filter(w => w.activeYesNo));
      setOrderNoERP(nextOrderNo.toString());

      // Set first warehouse as default if available
      if (warehousesData.length > 0 && warehousesData[0].activeYesNo) {
        setWarehouse(warehousesData[0].warehouseID);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Kunne ikke laste data. Prøv igjen.');
    } finally {
      setLoadingData(false);
    }
  };

  const addLine = () => {
    const newLine: OrderLineFormData = {
      id: `line-${Date.now()}`,
      itemNo: '',
      quantity: 1,
      description1: '',
      itemUnitCode: '',
      stdLocationName: '',
      warehouse: warehouse
    };
    setLines([...lines, newLine]);
  };

  const removeLine = (id: string) => {
    setLines(lines.filter(line => line.id !== id));
  };

  const updateLine = (id: string, field: keyof OrderLineFormData, value: any, item?: SearchableItem) => {
    setLines(lines.map(line => {
      if (line.id === id) {
        const updatedLine = { ...line, [field]: value };

        // Auto-populate fields when item is selected (item data comes from search callback)
        if (field === 'itemNo' && item) {
          updatedLine.description1 = item.description1 || '';
          updatedLine.itemUnitCode = item.itemUnitCode || '';
          updatedLine.stdLocationName = item.stdLocationName || '';
        }

        return updatedLine;
      }
      return line;
    }));
  };

  const validateForm = (): string | null => {
    if (!orderNoERP.trim()) {
      return 'Ordrenummer (ERP) er påkrevd';
    }

    if (!customerNo.trim()) {
      return 'Kundenummer er påkrevd';
    }

    if (lines.length === 0) {
      return 'Minst én ordrelinje er påkrevd';
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line.itemNo.trim()) {
        return `Varenummer er påkrevd på linje ${i + 1}`;
      }
      if (line.quantity <= 0) {
        return `Antall må være større enn 0 på linje ${i + 1}`;
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const request: CreateOrderWithLinesRequest = {
        orderNoERP: orderNoERP.trim(),
        customerNo: customerNo.trim(),
        customerName: customerName.trim() || undefined,
        orderDate: orderDate || undefined,
        deliveryDate: deliveryDate || undefined,
        warehouse: warehouse || undefined,
        delCustomerName: delCustomerName.trim() || undefined,
        delAddress1: delAddress1.trim() || undefined,
        delCity: delCity.trim() || undefined,
        delZipCode: delZipCode.trim() || undefined,
        lines: lines.map(line => ({
          itemNo: line.itemNo.trim(),
          quantity: line.quantity,
          description1: line.description1 || undefined,
          itemUnitCode: line.itemUnitCode || undefined,
          stdLocationName: line.stdLocationName || undefined,
          warehouse: line.warehouse || warehouse || undefined
        }))
      };

      const response = await orderRegistrationService.createOrderWithLines(request);

      if (response.success) {
        alert(`Ordre ${response.orderNo} opprettet!`);
        onSuccess(response.orderNo);

        // Reset form
        resetForm();
      } else {
        throw new Error(response.message || 'Kunne ikke opprette ordre');
      }
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke opprette ordre. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = async () => {
    setCustomerNo('');
    setCustomerName('');
    setOrderDate(new Date().toISOString().split('T')[0]);
    setDeliveryDate('');
    setDelCustomerName('');
    setDelAddress1('');
    setDelCity('');
    setDelZipCode('');
    setLines([]);

    // Get new OrderNo
    try {
      const nextOrderNo = await orderRegistrationService.getNextOrderNo();
      setOrderNoERP(nextOrderNo.toString());
    } catch (err) {
      console.error('Error getting next OrderNo:', err);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-blue-600" size={32} />
        <span className="ml-3 text-gray-600">Laster data...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      {/* Order Info */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ordreinfo</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordrenummer ERP
            </label>
            <input
              type="text"
              value={orderNoERP}
              onChange={(e) => setOrderNoERP(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lager
            </label>
            <select
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Velg lager...</option>
              {warehouses.map(w => (
                <option key={w.warehouseID} value={w.warehouseID}>
                  {w.warehouseDescr || w.warehouseID}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordredato
            </label>
            <input
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Leveringsdato
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Kundeinfo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kundenummer <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={customerNo}
              onChange={(e) => setCustomerNo(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Kundenummer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kundenavn
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Kundenavn"
            />
          </div>
        </div>
      </div>

      {/* Delivery Address */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Leveringsadresse</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Leveringskundenavn
            </label>
            <input
              type="text"
              value={delCustomerName}
              onChange={(e) => setDelCustomerName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Navn"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse
            </label>
            <input
              type="text"
              value={delAddress1}
              onChange={(e) => setDelAddress1(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Adresse"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postnummer
            </label>
            <input
              type="text"
              value={delZipCode}
              onChange={(e) => setDelZipCode(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Postnummer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sted
            </label>
            <input
              type="text"
              value={delCity}
              onChange={(e) => setDelCity(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Sted"
            />
          </div>
        </div>
      </div>

      {/* Order Lines */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Ordrelinjer</h3>
          <button
            type="button"
            onClick={addLine}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={16} />
            Legg til linje
          </button>
        </div>

        {lines.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Ingen ordrelinjer lagt til. Klikk på "Legg til linje" for å legge til.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Varenr <span className="text-red-600">*</span>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Beskrivelse
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Antall <span className="text-red-600">*</span>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Enhet
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Lokasjon
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-12">

                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lines.map((line) => (
                  <tr key={line.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 min-w-[220px]">
                      <ItemSearchSelect
                        value={line.itemNo}
                        onChange={(itemNo, item) => updateLine(line.id, 'itemNo', itemNo, item)}
                        selectedLabel={line.itemNo ? `${line.itemNo} - ${line.description1 || ''}` : ''}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.description1 || ''}
                        onChange={(e) => updateLine(line.id, 'description1', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Beskrivelse"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="1"
                        value={line.quantity}
                        onChange={(e) => updateLine(line.id, 'quantity', parseInt(e.target.value) || 0)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.itemUnitCode || ''}
                        onChange={(e) => updateLine(line.id, 'itemUnitCode', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enhet"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.stdLocationName || ''}
                        onChange={(e) => updateLine(line.id, 'stdLocationName', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Lokasjon"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeLine(line.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Fjern linje"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors font-medium"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Lagrer...
            </>
          ) : (
            <>
              <Save size={20} />
              Lagre Ordre
            </>
          )}
        </button>
      </div>
    </form>
  );
};
